import React, { useState } from 'react';
import { Button, Dropdown, MenuProps } from 'antd';
import { BaseButton, Icon, Modal } from '@common/components';
import { createMessage } from 'works/legacy/lib/message-helper';
import { useMutation } from '@common/hooks';
import { PerformWorksInvoiceAction } from '@pay-bill/graphql';

const feedbackMap: Record<string, string> = {
  void: 'voided',
  markPaid: 'marked Paid',
};

const actionMap: Record<string, string> = {
  void: 'void',
  pay: 'pay',
};

export const InvoiceActionsButton = ({
  invoice,
  styleType,
  onFinish,
}: {
  styleType: 'primary' | 'default';
  invoice: {
    id: string;
    state: string;
    canVoid: { value: boolean };
    canMarkPaid: { value: boolean };
  };
  onFinish?: () => void;
}) => {
  const [action, setAction] = useState<'markPaid' | 'void' | undefined>();
  const [loading, setLoading] = useState(false);
  const [confirmVisible, setConfirmVisible] = useState(false);
  const hasAnyPerm = invoice.canMarkPaid.value || invoice.canVoid.value;

  const [performActionMutation] = useMutation(PerformWorksInvoiceAction, {
    refetchQueries: ['InvoiceReviewQuery'],
    onCompleted: () => {
      createMessage('success', `Invoice successfully ${feedbackMap[action!]}.`);
      setAction(undefined);
      setLoading(false);
      setConfirmVisible(false);
      onFinish?.();
    },
    onError: () => {
      createMessage(
        'error',
        `An error occurred while trying to ${actionMap[action!]} the invoice`
      );
      setAction(undefined);
      setLoading(false);
      setConfirmVisible(false);
    },
  });

  const onClick: MenuProps['onClick'] = ({ key }) => {
    handleAction(key as 'markPaid' | 'void');
  };

  const items: MenuProps['items'] = [
    invoice.state !== 'draft'
      ? {
          label: 'Mark Paid',
          className: 'invoice-mark-paid-action',
          key: 'markPaid',
        }
      : null,
    {
      label: 'Void',
      className: 'invoice-void-action',
      key: 'void',
    },
  ];

  const handleAction = (action: 'markPaid' | 'void') => {
    setAction(action);
    setConfirmVisible(true);
  };

  const onActionConfirm = (action: 'markPaid' | 'void' | undefined) => {
    setLoading(true);
    const castAction = action === 'markPaid' ? 'mark_paid' : 'void';

    performActionMutation({
      variables: {
        invoiceIds: [invoice.id],
        action: castAction,
      },
    });
  };

  if (!hasAnyPerm) {
    return null;
  }

  return (
    <>
      <Dropdown className="invoice-actions-dropdown" menu={{ items, onClick }}>
        <Button
          loading={loading}
          type={styleType}
          style={{ marginLeft: styleType === 'primary' ? 16 : 0 }}
        >
          <span style={{ marginRight: 8 }}>Actions</span>
          <Icon icon="chevronDown" kind="light" />
        </Button>
      </Dropdown>
      <Modal
        visible={confirmVisible}
        title={`Are you sure you want to ${
          action === 'markPaid' ? 'mark this invoice paid' : 'void this invoice'
        }?`}
        onCancel={() => setConfirmVisible(false)}
        footer={
          <>
            <BaseButton
              type="ghost"
              className="invoice-action-cancel color-elm"
              onClick={() => setConfirmVisible(false)}
              disabled={loading}
            >
              No, Cancel
            </BaseButton>
            <BaseButton
              type="primary"
              className="invoice-action-confirm"
              onClick={() => onActionConfirm(action)}
              loading={loading}
            >
              Yes
            </BaseButton>
          </>
        }
      >
        This action cannot be undone.
      </Modal>
    </>
  );
};
